import type { PocInputError } from 'commons/types';
import {
    AdditionalOnlyValidator,
    AlphanumericAndAdditionalValidator,
    MaxLengthValidator,
    MaxSelectableValuesValidator,
    MaxValueValidator,
    MinLengthValidator,
    MinValueValidator,
    PropertyValidator,
    PropertyValidatorType,
    RegexValidator,
} from './settings-devices-configuration-layout.model';

const IPV4_REGEX = /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
const IPV6_REGEX =
    /^((?:[a-fA-F0-9]{1,4}:){7}[a-fA-F0-9]{1,4}|(::)|(([a-fA-F0-9]{1,4}:){1,7}:)|(::([a-fA-F0-9]{1,4}:){0,6}[a-fA-F0-9]{1,4}))$/;
const HEX_REGEX = /^[0-9a-fA-F]+$/;
const DECIMAL_REGEX = /^-?\d+(\.\d+)?$/;
const UPPER_CASE_REGEX = /^[A-Z]+$/;
const LOWER_CASE_REGEX = /^[a-z]+$/;
const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9]+$/;

export type ValidationResult = { isValid: boolean; error: PocInputError };
export type ValidationFunction = (value: string, validator: PropertyValidator) => ValidationResult;
export type ValidationMultiValueFunction = (value: string[], validator: PropertyValidator) => ValidationResult;

export const minLengthValidator: ValidationFunction = (value: string, validator: PropertyValidator) => ({
    isValid: value.length > (validator as MinLengthValidator).length,
    error: 'too-short',
});

export const maxLengthValidator: ValidationFunction = (value: string, validator: PropertyValidator) => ({
    isValid: value.length <= (validator as MaxLengthValidator).length,
    error: 'too-long',
});

export const minValueValidator: ValidationFunction = (value: string, validator: PropertyValidator) => ({
    isValid: Number(value) >= (validator as MinValueValidator).value,
    error: 'range-underflow',
});

export const maxValueValidator: ValidationFunction = (value: string, validator: PropertyValidator) => ({
    isValid: Number(value) <= (validator as MaxValueValidator).value,
    error: 'range-overflow',
});

export const regexValidator: ValidationFunction = (value: string, validator: PropertyValidator) => ({
    isValid: new RegExp((validator as RegexValidator).regex).test(value),
    error: 'pattern-mismatch',
});

export const upperCaseValidator: ValidationFunction = (value: string) => ({
    isValid: UPPER_CASE_REGEX.test(value),
    error: 'pattern-mismatch',
});

export const lowerCaseValidator: ValidationFunction = (value: string) => ({
    isValid: LOWER_CASE_REGEX.test(value),
    error: 'pattern-mismatch',
});

export const ipAddressValidator: ValidationFunction = (value: string) => ({
    isValid: IPV4_REGEX.test(value) || IPV6_REGEX.test(value),
    error: 'pattern-mismatch',
});

export const hexadecimalValidator: ValidationFunction = (value: string) => ({
    isValid: HEX_REGEX.test(value),
    error: 'type-mismatch',
});

export const decimalValidator: ValidationFunction = (value: string) => ({
    isValid: DECIMAL_REGEX.test(value),
    error: 'type-mismatch',
});

export const additionalOnlyValidator: ValidationFunction = (value: string, validator: PropertyValidator) => ({
    isValid: (validator as AdditionalOnlyValidator).values.includes(value),
    error: 'type-mismatch',
});

export const alphanumericAndAdditionalValidator: ValidationFunction = (
    value: string,
    validator: PropertyValidator
) => ({
    isValid: [...value].every(
        (char) =>
            ALPHANUMERIC_REGEX.test(char) ||
            (validator as AlphanumericAndAdditionalValidator).extraValues.includes(char)
    ),
    error: 'pattern-mismatch',
});

export const maxSelectableValuesValidator: ValidationMultiValueFunction = (
    value: string[],
    validator: PropertyValidator
) => ({
    isValid: value.length <= (validator as MaxSelectableValuesValidator).maximum,
    error: 'value-not-selectable',
});

export const alphanumericValidator: ValidationFunction = (value: string) => ({
    isValid: ALPHANUMERIC_REGEX.test(value),
    error: 'pattern-mismatch',
});

export const validations: Record<PropertyValidatorType, ValidationMultiValueFunction | ValidationFunction> = {
    MIN_LENGTH: minLengthValidator,
    MAX_LENGTH: maxLengthValidator,
    MIN_VALUE: minValueValidator,
    MAX_VALUE: maxValueValidator,
    REGEX: regexValidator,
    UPPER_CASE: upperCaseValidator,
    LOWER_CASE: lowerCaseValidator,
    IP_ADDRESS: ipAddressValidator,
    HEXADECIMAL: hexadecimalValidator,
    ALPHANUMERIC: alphanumericValidator,
    DECIMAL: decimalValidator,
    ADDITIONAL_ONLY: additionalOnlyValidator,
    ALPHANUMERIC_AND_ADDITIONAL: alphanumericAndAdditionalValidator,
    MAX_SELECTABLE_VALUES: maxSelectableValuesValidator,
};
